/**
 * The quote editor models a quote as character ranges of the review's text, so whole-sentence taps
 * and word selections combine in any mix and the result is always the reviewer's own words.
 * Positions are offsets into `reviewText(content)`; every range is [start, end).
 */

export const ELLIPSIS = "…";
/** Characters on the image; the server holds edits to the same limit. */
export const MAX_QUOTE_LENGTH = 180;
/** A person may skip two parts of the review (three passages); the AI keeps to one skip. */
export const MAX_PASSAGES = 3;
export const MAX_PHRASES = 2;
export const MAX_PHRASE_WORDS = 6;

export interface TextRange {
  start: number;
  end: number;
}

const WORD_CHAR = /[\p{L}\p{N}]/u;
const isWordChar = (c: string | undefined) => c != null && WORD_CHAR.test(c);
const hasWord = (text: string) => WORD_CHAR.test(text);

/** The review as the editor shows it: whitespace collapsed, typography kept so the image keeps it too. */
export function reviewText(content: string | null | undefined): string {
  return String(content ?? "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Curly quote marks read as straight ones when matching, as the server compares. One character for one, so offsets hold. */
function comparable(text: string): string {
  return text.replace(/[‘’]/g, "'").replace(/[“”]/g, '"');
}

/** The server's sanitizer: whitespace collapsed, one pair of wrapping quote marks dropped, every " … " join spaced the same. */
export function sanitizeQuote(quote: string | null | undefined): string {
  let text = reviewText(quote);
  if (
    text.length >= 2 &&
    ((text.startsWith('"') && text.endsWith('"')) || (text.startsWith("“") && text.endsWith("”")))
  ) {
    text = text.slice(1, -1).trim();
  }
  return text.replace(/\s*…\s*/g, ` ${ELLIPSIS} `).trim();
}

/** Characters as the server counts them (an emoji is one). */
export const quoteLength = (quote: string) => Array.from(sanitizeQuote(quote)).length;

/**
 * Each sentence's range in `h`. A sentence ends after ".", "!" or "?" followed by a space, so an
 * abbreviation like "Mr." also ends one; that only means the reviewer's sentence is two taps.
 */
export function sentenceSpans(h: string): TextRange[] {
  const spans: TextRange[] = [];
  let start = 0;
  for (let i = 0; i < h.length; i++) {
    if ((h[i] === "." || h[i] === "!" || h[i] === "?") && h[i + 1] === " ") {
      spans.push({ start, end: i + 1 });
      start = i + 2;
    }
  }
  if (start < h.length) spans.push({ start, end: h.length });
  return spans.filter(span => span.end > span.start);
}

export const splitSentences = (content: string | null | undefined): string[] => {
  const h = reviewText(content);
  return sentenceSpans(h).map(span => h.slice(span.start, span.end));
};

/** Overlapping ranges, and ranges with only punctuation or spaces between them, become one; sorted. */
export function mergeRanges(h: string, ranges: TextRange[]): TextRange[] {
  const sorted = ranges.map(r => ({ ...r })).sort((a, b) => a.start - b.start);
  const out: TextRange[] = [];
  for (const range of sorted) {
    const last = out[out.length - 1];
    if (last && (range.start <= last.end || !hasWord(h.slice(last.end, range.start))))
      last.end = Math.max(last.end, range.end);
    else out.push(range);
  }
  return out;
}

function cleanCut(h: string, at: number, needle: string): boolean {
  const before = at === 0 ? undefined : h[at - 1];
  const after = h[at + needle.length];
  return (
    (!isWordChar(before) || !isWordChar(needle[0])) && (!isWordChar(after) || !isWordChar(needle[needle.length - 1]))
  );
}

function findPassage(h: string, needle: string, from: number): number | null {
  const candidates = [needle];
  // The analyzer may capitalize a passage that starts mid-sentence.
  if (needle.length > 1) {
    const first = needle[0];
    const swapped = first === first.toUpperCase() ? first.toLowerCase() : first.toUpperCase();
    if (swapped !== first) candidates.push(swapped + needle.slice(1));
  }
  for (const candidate of candidates) {
    let at = h.indexOf(candidate, from);
    while (at !== -1) {
      if (cleanCut(h, at, candidate)) return at;
      at = h.indexOf(candidate, at + 1);
    }
  }
  return null;
}

/**
 * Where a quote's passages sit in `h`, in order. A passage that isn't in the review (a coach's own
 * wording, say) is left out, and an added final period is forgiven as the server forgives it.
 */
export function locateRanges(h: string, quote: string | null | undefined): TextRange[] {
  const haystack = comparable(h);
  const passages = sanitizeQuote(quote)
    .split(ELLIPSIS)
    .map(p => comparable(p.trim()))
    .filter(Boolean);
  const out: TextRange[] = [];
  let from = 0;
  for (const passage of passages) {
    const tries = [passage, passage.replace(/\s*[.!?]$/, "")].filter((p, i, all) => p && all.indexOf(p) === i);
    for (const needle of tries) {
      const at = findPassage(haystack, needle, from) ?? findPassage(haystack, needle, 0);
      if (at == null) continue;
      out.push({ start: at, end: at + needle.length });
      from = at + needle.length;
      break;
    }
  }
  return mergeRanges(h, out);
}

/** The quote the ranges make: passages in review order joined by " … "; null past the passage limit. */
export function quoteFromRanges(h: string, ranges: TextRange[]): string | null {
  const groups = mergeRanges(h, ranges);
  if (groups.length > MAX_PASSAGES) return null;
  // A reviewer's own ellipsis at the edge of a passage would sit beside the join and read as an empty
  // passage, so it goes; one inside a passage stays, and the server doesn't count it as a skip.
  return groups.map(r => h.slice(r.start, r.end).replace(/^[\s…]+|[\s…]+$/g, "")).join(` ${ELLIPSIS} `);
}

/** A range with leading non-words and trailing spaces dropped, so a piece starts on a word. */
function trimRange(h: string, range: TextRange): TextRange {
  let { start, end } = range;
  while (start < end && !isWordChar(h[start])) start++;
  while (end > start && /\s/.test(h[end - 1])) end--;
  return { start, end };
}

export function subtractRange(h: string, ranges: TextRange[], cut: TextRange): TextRange[] {
  const out: TextRange[] = [];
  for (const r of ranges) {
    if (r.end <= cut.start || r.start >= cut.end) {
      out.push(r);
      continue;
    }
    if (r.start < cut.start) out.push({ start: r.start, end: cut.start });
    if (r.end > cut.end) out.push({ start: cut.end, end: r.end });
  }
  return out.map(r => trimRange(h, r)).filter(r => r.end > r.start && hasWord(h.slice(r.start, r.end)));
}

export type Coverage = "on" | "part" | "";

/** "on" when every word of the sentence is in the quote, "part" when some are. */
export function coverage(h: string, sentence: TextRange, ranges: TextRange[]): Coverage {
  const text = h.slice(sentence.start, sentence.end);
  const lead = text.match(/^[^\p{L}\p{N}]*/u)?.[0].length ?? 0;
  const tail = text.match(/[^\p{L}\p{N}]*$/u)?.[0].length ?? 0;
  const wordsStart = sentence.start + lead;
  const wordsEnd = Math.max(wordsStart, sentence.end - tail);
  if (ranges.some(r => r.start <= wordsStart && r.end >= wordsEnd)) return "on";
  return ranges.some(r => r.start < wordsEnd && r.end > wordsStart) ? "part" : "";
}

/** A selection of the review widened to whole words and trimmed; null when it holds no word. */
export function widenToWords(h: string, range: TextRange): TextRange | null {
  let start = Math.max(0, Math.min(range.start, h.length));
  let end = Math.max(start, Math.min(range.end, h.length));
  while (start > 0 && isWordChar(h[start - 1]) && isWordChar(h[start])) start--;
  while (end < h.length && isWordChar(h[end]) && isWordChar(h[end - 1])) end++;
  const trimmed = trimRange(h, { start, end });
  return trimmed.end > trimmed.start && hasWord(h.slice(trimmed.start, trimmed.end)) ? trimmed : null;
}

// ---- highlights: the quote as tokens, picks as token indexes ----

export const tokenize = (quote: string | null | undefined): string[] => {
  const text = sanitizeQuote(quote);
  return text ? text.split(" ") : [];
};

const isGap = (token: string) => token === ELLIPSIS;
const bareToken = (token: string) => token.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, "").toLowerCase();

/** Runs of neighboring picked tokens; each run is one phrase. */
export function runsOf(selected: Iterable<number>): number[][] {
  const runs: number[][] = [];
  [...selected]
    .sort((a, b) => a - b)
    .forEach(i => {
      const run = runs[runs.length - 1];
      if (run && run[run.length - 1] === i - 1) run.push(i);
      else runs.push([i]);
    });
  return runs;
}

export function phrasesFromSelection(tokens: string[], selected: Iterable<number>): string[] {
  return runsOf(selected)
    .map(run =>
      run
        .map(i => tokens[i])
        .join(" ")
        .replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, "")
    )
    .filter(Boolean);
}

/** The tokens each phrase covers, matched case-insensitively at its first occurrence; phrases not in the quote are dropped. */
export function selectionFromPhrases(tokens: string[], phrases: string[] | null | undefined): Set<number> {
  const selected = new Set<number>();
  (phrases ?? []).forEach(phrase => {
    const words = reviewText(phrase).split(" ").map(bareToken).filter(Boolean);
    if (!words.length) return;
    for (let i = 0; i + words.length <= tokens.length; i++) {
      if (words.every((word, j) => bareToken(tokens[i + j]) === word)) {
        words.forEach((_, j) => selected.add(i + j));
        return;
      }
    }
  });
  return selected;
}

export const withinPhraseLimits = (selected: Set<number>) => {
  const runs = runsOf(selected);
  return runs.length <= MAX_PHRASES && runs.every(run => run.length <= MAX_PHRASE_WORDS);
};

/**
 * Whether a change to the picks is allowed: it keeps them within two phrases of six words, or it only
 * takes words away without making more phrases (the review's own highlights may be longer, and
 * trimming them is always fine).
 */
export function allowedPhraseChange(selected: Set<number>, next: Set<number>): boolean {
  if (withinPhraseLimits(next)) return true;
  const onlyRemoves = [...next].every(i => selected.has(i));
  return onlyRemoves && runsOf(next).length <= runsOf(selected).length;
}

/** Whether tapping a word is allowed under the phrase limits. */
export function canToggle(tokens: string[], selected: Set<number>, index: number): boolean {
  if (index < 0 || index >= tokens.length || isGap(tokens[index])) return false;
  const next = new Set(selected);
  if (next.has(index)) next.delete(index);
  else next.add(index);
  return allowedPhraseChange(selected, next);
}

/** The phrases still in a quote: what the editor and the server keep when the quote changes. */
export const phrasesInQuote = (quote: string, phrases: string[] | null | undefined): string[] => {
  const tokens = tokenize(quote);
  return phrasesFromSelection(tokens, selectionFromPhrases(tokens, phrases));
};

const phraseKey = (phrases: string[]) =>
  phrases
    .map(p => reviewText(p).toLowerCase())
    .sort()
    .join("|");

/** Same quote and the same highlights (any order or casing). */
export function sameQuote(
  a: { quote: string | null | undefined; phrases: string[] | null | undefined },
  b: { quote: string | null | undefined; phrases: string[] | null | undefined }
): boolean {
  const quoteA = sanitizeQuote(a.quote);
  const quoteB = sanitizeQuote(b.quote);
  return (
    quoteA === quoteB && phraseKey(phrasesInQuote(quoteA, a.phrases)) === phraseKey(phrasesInQuote(quoteB, b.phrases))
  );
}

/** A review has words to pick from; a rating-only review doesn't. */
export const hasWrittenReview = (content: string | null | undefined) => hasWord(reviewText(content));
